import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import useProcessFlowStore from '../../stores/processFlowStore';
import { useSnackbar } from 'notistack';

function ShareDialog({ flowId, open, onClose }) {
  const { currentFlow, shareProcessFlow, unshareProcessFlow } = useProcessFlowStore();
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');

  const handleShare = async () => {
    if (!email) {
      enqueueSnackbar('Please enter an email address', { variant: 'warning' });
      return;
    }

    try {
      await shareProcessFlow(flowId, { email, role });
      enqueueSnackbar('Process flow shared successfully', { variant: 'success' });
      setEmail('');
      setRole('viewer');
    } catch (error) {
      enqueueSnackbar('Failed to share process flow', { variant: 'error' });
    }
  };

  const handleUnshare = async (userId) => {
    try {
      await unshareProcessFlow(flowId, userId);
      enqueueSnackbar('Access removed successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to remove access', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share Process Flow
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add People
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                label="Role"
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="collaborator">Collaborator</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleShare}>
              Share
            </Button>
          </Box>
        </Box>

        {currentFlow?.shares && currentFlow.shares.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              People with Access
            </Typography>
            <List>
              {currentFlow.shares.map((share) => (
                <ListItem key={share.userId?._id || share.email} divider>
                  <ListItemText
                    primary={share.userId?.firstName && share.userId?.lastName
                      ? `${share.userId.firstName} ${share.userId.lastName}`
                      : share.email}
                    secondary={share.userId?.email}
                  />
                  <Chip
                    label={share.role}
                    size="small"
                    color={share.role === 'admin' ? 'primary' : 'default'}
                    sx={{ mr: 2 }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="remove"
                      onClick={() => handleUnshare(share.userId?._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShareDialog;